// @ts-nocheck
import { Switch, Typography, App } from "../components/admin-ui";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "../components/admin-ui";
import { useFeatureFlags, useToggleFlag } from "../api/hooks";
import { AdminShell } from "../layouts/AdminShell";
import { DataTable } from "../components/DataTable";
import { CheckCircleOutlined, CloseCircleOutlined, SafetyCertificateOutlined } from "../components/admin-icons";
import { tokens } from "../styles/tokens";

const { Text } = Typography;
const { color: T } = tokens;

interface Flag { id: string; key: string; enabled: boolean; note: string | null }

export function FeatureFlagsScreen() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { data, isLoading } = useFeatureFlags();
  const toggle = useToggleFlag();
  const flags = data?.data ?? [];
  const enabledCount = flags.filter((flag) => flag.enabled).length;
  const disabledCount = flags.length - enabledCount;

  const onChange = async (key: string, enabled: boolean) => {
    try {
      await toggle.mutateAsync({ key, enabled });
      void message.success(enabled ? t("featureFlags.flagEnabled", { key }) : t("featureFlags.flagDisabled", { key }));
    } catch {
      void message.error(t("featureFlags.updateFailed"));
    }
  };

  const columns: ColumnsType<Flag> = [
    {
      title: t("featureFlags.colFlagKey"), dataIndex: "key", key: "key", width: 240,
      render: (v) => <Text code className="admin-feature-flag-key admin-data-code">{v}</Text>,
    },
    {
      title: t("featureFlags.colDescription"), dataIndex: "note", key: "desc",
      render: (_v, row) => {
        const localized = t(`featureFlags.desc_${row.key}`, { defaultValue: "" });
        const label = localized || row.note;
        return label
          ? <span className="admin-feature-flag-description">{label}</span>
          : <Text type="secondary" className="admin-feature-flag-description">{t("featureFlags.noDescription")}</Text>;
      },
    },
    {
      title: t("featureFlags.colEnabled"), dataIndex: "enabled", key: "enabled", width: 100, align: "center",
      render: (v: boolean, row) => (
        <Switch
          checked={v}
          loading={toggle.isPending}
          onChange={(checked) => void onChange(row.key, checked)}
          checkedChildren="ON"
          unCheckedChildren="OFF"
          aria-label={t("featureFlags.toggleLabel", { key: row.key })}
        />
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="admin-feature-flags-page admin-list-page">
        <div className="admin-feature-flags-inner">
          <div className="admin-feature-flags-toolbar">
            <div>
              <h1 className="admin-page-title">{t("featureFlags.title")}</h1>
              <p className="admin-feature-flags-subtitle">{t("featureFlags.pageSubtitle")}</p>
            </div>
          </div>

          <div className="admin-feature-flags-stats admin-txn-stats grid">
            {[
              { label: t("featureFlags.statTotal"), value: flags.length, note: t("featureFlags.statBadgeTotal"), icon: <SafetyCertificateOutlined />, tone: "total" },
              { label: t("featureFlags.statEnabled"), value: enabledCount, note: t("featureFlags.statBadgeEnabled"), icon: <CheckCircleOutlined />, tone: "enabled" },
              { label: t("featureFlags.statDisabled"), value: disabledCount, note: t("featureFlags.statBadgeDisabled"), icon: <CloseCircleOutlined />, tone: "disabled" },
            ].map((stat) => (
              <div className={`admin-kpi-card admin-feature-flag-stat admin-feature-flag-stat-${stat.tone}`} key={stat.tone}>
                <div className="admin-kpi-heading flex">
                  <div className="admin-kpi-label">{stat.label}</div>
                  <span className="admin-kpi-icon">{stat.icon}</span>
                </div>
                <div className="admin-kpi-value">{stat.value.toLocaleString()}</div>
                <div className="admin-feature-flag-stat-note">{stat.note}</div>
              </div>
            ))}
          </div>

          <p className="admin-feature-flags-results">{t("common.total", { count: flags.length })}</p>
          <div className="admin-feature-flags-table-panel admin-data-table-panel">
            <DataTable<Flag>
              columns={columns}
              dataSource={flags}
              loading={isLoading}
              rowKey="key"
              sticky={false}
              pagination={false}
            />
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
